import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { 
    Users, UserCheck, ShoppingCart, Clock, Check, X, Trash2, 
    Eye, ChevronDown, ChevronUp, RefreshCw, UsersRound 
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegistrationsTab = ({ tournaments }) => {
    const [registrations, setRegistrations] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ tournament_id: 'all', status: 'all', type: 'all' });
    const [expandedRow, setExpandedRow] = useState(null);
    const [selectedRegistration, setSelectedRegistration] = useState(null);
    const [showDetailDialog, setShowDetailDialog] = useState(false);

    const loadRegistrations = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = {};
            if (filter.tournament_id !== 'all') params.tournament_id = filter.tournament_id;
            if (filter.status !== 'all') params.status = filter.status;
            if (filter.type !== 'all') params.registration_type = filter.type;

            const [regRes, statsRes] = await Promise.all([
                axios.get(`${API_URL}/api/admin/registrations`, {
                    params,
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_URL}/api/admin/registrations/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);

            setRegistrations(regRes.data.registrations || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Failed to load registrations:', error);
            toast.error('Failed to load registrations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRegistrations();
    }, [filter]);

    const handleUpdateStatus = async (regId, newStatus) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_URL}/api/admin/registrations/${regId}/status?status=${newStatus}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Registration ${newStatus}`);
            loadRegistrations();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleDelete = async (regId) => {
        if (!window.confirm('Are you sure you want to delete this registration?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/api/admin/registrations/${regId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Registration deleted');
            loadRegistrations();
        } catch (error) {
            toast.error('Failed to delete registration');
        }
    };

    const viewDetails = (reg) => {
        setSelectedRegistration(reg);
        setShowDetailDialog(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'confirmed': return 'bg-success/20 text-success';
            case 'pending': return 'bg-warning/20 text-warning';
            case 'in_cart': return 'bg-blue-500/20 text-blue-400';
            case 'cancelled': return 'bg-destructive/20 text-destructive';
            default: return 'bg-white/10 text-muted-foreground';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <div className="bg-[#0d2847] border border-white/5 p-4">
                        <Users className="w-5 h-5 text-primary mb-2" />
                        <p className="stat-display text-2xl">{stats.total_registrations}</p>
                        <p className="text-xs text-muted-foreground">Total Registrations</p>
                    </div>
                    <div className="bg-[#0d2847] border border-white/5 p-4">
                        <UserCheck className="w-5 h-5 text-success mb-2" />
                        <p className="stat-display text-2xl">{stats.confirmed}</p>
                        <p className="text-xs text-muted-foreground">Confirmed</p>
                    </div>
                    <div className="bg-[#0d2847] border border-white/5 p-4">
                        <Clock className="w-5 h-5 text-warning mb-2" />
                        <p className="stat-display text-2xl">{stats.pending}</p>
                        <p className="text-xs text-muted-foreground">Pending</p>
                    </div>
                    <div className="bg-[#0d2847] border border-white/5 p-4">
                        <ShoppingCart className="w-5 h-5 text-blue-400 mb-2" />
                        <p className="stat-display text-2xl">{stats.in_cart}</p>
                        <p className="text-xs text-muted-foreground">In Cart</p>
                    </div>
                    <div className="bg-[#0d2847] border border-white/5 p-4">
                        <UsersRound className="w-5 h-5 text-purple-400 mb-2" />
                        <p className="stat-display text-2xl">{stats.team_registrations}</p>
                        <p className="text-xs text-muted-foreground">Team Registrations</p>
                    </div>
                    <div className="bg-[#0d2847] border border-white/5 p-4">
                        <Users className="w-5 h-5 text-cyan-400 mb-2" />
                        <p className="stat-display text-2xl">{stats.individual_registrations}</p>
                        <p className="text-xs text-muted-foreground">Individual</p>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-4 p-4 bg-[#0d2847] border border-white/5 items-center">
                <Select value={filter.tournament_id} onValueChange={(v) => setFilter({...filter, tournament_id: v})}>
                    <SelectTrigger className="w-64 bg-[#0d2847] border-white/10">
                        <SelectValue placeholder="Tournament" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d2847] border-white/10 max-h-60">
                        <SelectItem value="all">All Tournaments</SelectItem>
                        {tournaments?.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={filter.status} onValueChange={(v) => setFilter({...filter, status: v})}>
                    <SelectTrigger className="w-40 bg-[#0d2847] border-white/10">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d2847] border-white/10">
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_cart">In Cart</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filter.type} onValueChange={(v) => setFilter({...filter, type: v})}>
                    <SelectTrigger className="w-40 bg-[#0d2847] border-white/10">
                        <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d2847] border-white/10">
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="team">Team</SelectItem>
                        <SelectItem value="individual">Individual</SelectItem>
                    </SelectContent>
                </Select>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={loadRegistrations}
                    className="border-white/10"
                >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
                <span className="text-sm text-muted-foreground ml-auto">
                    {registrations.length} registrations
                </span>
            </div>

            {/* Registrations Table */}
            <div className="bg-[#0d2847] border border-white/5 overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-[#0d2847]">
                        <tr>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4 w-8"></th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Registrant</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Tournament</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Type</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Category</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Fee</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Date</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Status</th>
                            <th className="text-right text-xs tracking-widest uppercase text-muted-foreground p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {registrations.map((reg) => (
                            <React.Fragment key={reg.id}>
                                <tr className="border-t border-white/5 hover:bg-white/5">
                                    <td className="p-4">
                                        {reg.registration_type === 'team' && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setExpandedRow(expandedRow === reg.id ? null : reg.id)}
                                                className="p-1 h-6 w-6"
                                            >
                                                {expandedRow === reg.id ? 
                                                    <ChevronUp className="w-4 h-4" /> : 
                                                    <ChevronDown className="w-4 h-4" />
                                                }
                                            </Button>
                                        )}
                                    </td>
                                    <td className="p-4">
                                        <div>
                                            <p className="font-medium">
                                                {reg.registration_type === 'team' ? reg.team_name : reg.athlete_name}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {reg.registration_type === 'team' ? 
                                                    `${reg.player_count || reg.players?.length || 0} players • ${reg.registered_by}` : 
                                                    reg.disciplines?.join(', ') || 'Individual'
                                                }
                                            </p>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <div>
                                            <p className="text-sm">{reg.tournament_name || 'Unknown'}</p>
                                            <p className="text-xs text-muted-foreground">{reg.sport}</p>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-2 py-1 text-xs uppercase ${
                                            reg.registration_type === 'team' ? 'bg-purple-500/20 text-purple-400' : 'bg-cyan-500/20 text-cyan-400'
                                        }`}>
                                            {reg.registration_type === 'team' ? 'Team' : 'Individual'}
                                        </span>
                                    </td>
                                    <td className="p-4 text-sm">{reg.age_category || '-'}</td>
                                    <td className="p-4">
                                        <div className="space-y-0.5">
                                            {reg.fee_waiver_type && reg.fee_waiver_type !== 'none' ? (
                                                <>
                                                    <span className="font-mono text-primary">
                                                        KSh {(reg.final_fee || 0).toLocaleString()}
                                                    </span>
                                                    {reg.base_fee && reg.base_fee !== reg.final_fee && (
                                                        <span className="text-xs text-muted-foreground line-through ml-2">
                                                            {reg.base_fee.toLocaleString()}
                                                        </span>
                                                    )}
                                                    <div>
                                                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                                                            reg.fee_waiver_type === 'full' 
                                                                ? 'bg-green-500/20 text-green-400' 
                                                                : 'bg-amber-500/20 text-amber-400'
                                                        }`}>
                                                            {reg.fee_waiver_type === 'full' ? '100% Free' : `-${reg.discount_applied || reg.fee_waiver_amount || 0} KSH`}
                                                        </span>
                                                    </div>
                                                </>
                                            ) : (
                                                <span className="font-mono text-primary">
                                                    {reg.fee || reg.final_fee || reg.base_fee ? `KSh ${(reg.fee || reg.final_fee || reg.base_fee).toLocaleString()}` : '-'}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4 text-xs text-muted-foreground">
                                        {formatDate(reg.registration_date)}
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-2 py-1 text-xs uppercase ${getStatusColor(reg.status)}`}>
                                            {reg.status}
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center justify-end gap-1">
                                            <Button size="sm" variant="ghost" onClick={() => viewDetails(reg)} className="text-muted-foreground hover:text-white">
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                            {reg.status !== 'confirmed' && (
                                                <Button size="sm" variant="ghost" onClick={() => handleUpdateStatus(reg.id, 'confirmed')} className="text-success hover:text-success">
                                                    <Check className="w-4 h-4" />
                                                </Button>
                                            )}
                                            {reg.status !== 'cancelled' && (
                                                <Button size="sm" variant="ghost" onClick={() => handleUpdateStatus(reg.id, 'cancelled')} className="text-warning hover:text-warning">
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            )}
                                            <Button size="sm" variant="ghost" onClick={() => handleDelete(reg.id)} className="text-destructive hover:text-destructive">
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                                {/* Expanded row for team details */}
                                {expandedRow === reg.id && reg.registration_type === 'team' && (
                                    <tr className="bg-[#0a1628]">
                                        <td colSpan="9" className="p-4">
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                                {/* Coach Details */}
                                                <div className="bg-[#0d2847] p-4 border border-white/10">
                                                    <h4 className="text-sm font-bold text-primary mb-3">Coach / Manager</h4>
                                                    <div className="space-y-1 text-sm">
                                                        <p><span className="text-muted-foreground">Name:</span> {reg.coach?.name}</p>
                                                        <p><span className="text-muted-foreground">Mobile:</span> {reg.coach?.mobile_number}</p>
                                                        {reg.coach?.email && <p><span className="text-muted-foreground">Email:</span> {reg.coach?.email}</p>}
                                                    </div>
                                                </div>
                                                {/* Players List */}
                                                <div className="bg-[#0d2847] p-4 border border-white/10">
                                                    <h4 className="text-sm font-bold text-primary mb-3">Players ({reg.players?.length || 0})</h4>
                                                    <div className="max-h-48 overflow-y-auto space-y-2">
                                                        {reg.players?.map((player, idx) => (
                                                            <div key={idx} className="flex items-center justify-between text-sm py-1 border-b border-white/5 last:border-0">
                                                                <div>
                                                                    <span className="text-muted-foreground mr-2">{idx + 1}.</span>
                                                                    <span>{player.name}</span>
                                                                </div>
                                                                <div className="text-xs text-muted-foreground">
                                                                    {player.date_of_birth} • {player.affiliation_name || player.affiliation_type}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                {registrations.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                        {loading ? 'Loading registrations...' : 'No registrations found'}
                    </div>
                )}
            </div>

            {/* Registration Detail Dialog */}
            <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
                <DialogContent className="bg-[#0d2847] border border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl uppercase">
                            Registration Details
                        </DialogTitle>
                    </DialogHeader>
                    {selectedRegistration && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-muted-foreground">Type</p>
                                    <p className="font-medium">{selectedRegistration.registration_type === 'team' ? 'Team' : 'Individual'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Status</p>
                                    <span className={`px-2 py-1 text-xs uppercase ${getStatusColor(selectedRegistration.status)}`}>
                                        {selectedRegistration.status}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Tournament</p>
                                    <p className="font-medium">{selectedRegistration.tournament_name}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Sport</p>
                                    <p className="font-medium">{selectedRegistration.sport}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">{selectedRegistration.registration_type === 'team' ? 'Team Name' : 'Athlete'}</p>
                                    <p className="font-medium">{selectedRegistration.registration_type === 'team' ? selectedRegistration.team_name : selectedRegistration.athlete_name}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Age Category</p>
                                    <p className="font-medium">{selectedRegistration.age_category || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Fee</p>
                                    <div className="space-y-1">
                                        {selectedRegistration.fee_waiver_type && selectedRegistration.fee_waiver_type !== 'none' ? (
                                            <>
                                                <p className="font-mono text-primary">KSh {(selectedRegistration.final_fee || 0).toLocaleString()}</p>
                                                {selectedRegistration.base_fee && (
                                                    <p className="text-xs text-muted-foreground line-through">
                                                        Was: KSh {selectedRegistration.base_fee.toLocaleString()}
                                                    </p>
                                                )}
                                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                                    selectedRegistration.fee_waiver_type === 'full' 
                                                        ? 'bg-green-500/20 text-green-400' 
                                                        : 'bg-amber-500/20 text-amber-400'
                                                }`}>
                                                    {selectedRegistration.fee_waiver_type === 'full' ? '100% Waived' : `${selectedRegistration.discount_applied || selectedRegistration.fee_waiver_amount || 0} KSH Discount`}
                                                </span>
                                            </>
                                        ) : (
                                            <p className="font-mono text-primary">
                                                {selectedRegistration.fee || selectedRegistration.final_fee || selectedRegistration.base_fee 
                                                    ? `KSh ${(selectedRegistration.fee || selectedRegistration.final_fee || selectedRegistration.base_fee).toLocaleString()}` 
                                                    : '-'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Registration Date</p>
                                    <p className="font-medium">{formatDate(selectedRegistration.registration_date)}</p>
                                </div>
                            </div>

                            {selectedRegistration.disciplines?.length > 0 && (
                                <div>
                                    <p className="text-xs text-muted-foreground mb-2">Disciplines</p>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedRegistration.disciplines.map((d, i) => (
                                            <span key={i} className="px-2 py-1 bg-white/5 text-sm">{d}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedRegistration.registration_type === 'team' && (
                                <>
                                    <div className="border-t border-white/10 pt-4">
                                        <h4 className="font-bold text-primary mb-3">Coach / Manager</h4>
                                        <div className="grid grid-cols-3 gap-4 text-sm">
                                            <div>
                                                <p className="text-xs text-muted-foreground">Name</p>
                                                <p>{selectedRegistration.coach?.name}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Mobile</p>
                                                <p>{selectedRegistration.coach?.mobile_number}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Email</p>
                                                <p>{selectedRegistration.coach?.email || '-'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="border-t border-white/10 pt-4">
                                        <h4 className="font-bold text-primary mb-3">Players ({selectedRegistration.players?.length || 0})</h4>
                                        <div className="max-h-64 overflow-y-auto">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="border-b border-white/10">
                                                        <th className="text-left p-2 text-xs text-muted-foreground">#</th>
                                                        <th className="text-left p-2 text-xs text-muted-foreground">Name</th>
                                                        <th className="text-left p-2 text-xs text-muted-foreground">DOB</th>
                                                        <th className="text-left p-2 text-xs text-muted-foreground">Affiliation</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedRegistration.players?.map((player, idx) => (
                                                        <tr key={idx} className="border-b border-white/5">
                                                            <td className="p-2 text-muted-foreground">{idx + 1}</td>
                                                            <td className="p-2">{player.name}</td>
                                                            <td className="p-2 text-muted-foreground">{player.date_of_birth}</td>
                                                            <td className="p-2 text-muted-foreground">
                                                                {player.affiliation_type === 'private' ? 'Private' : player.affiliation_name}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            )}

                            {selectedRegistration.athlete_profile && (
                                <div className="border-t border-white/10 pt-4">
                                    <h4 className="font-bold text-primary mb-3">Athlete Profile</h4>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <p className="text-xs text-muted-foreground">Country</p>
                                            <p>{selectedRegistration.athlete_profile.country || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground">School</p>
                                            <p>{selectedRegistration.athlete_profile.school || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default RegistrationsTab;
